import {
  AdminInputPlaceholder,
  Callout,
  Disclaimer,
  Div,
  getVDriveFleetOrganizations,
  lightTheme,
  reportError,
} from '@vgrubs/components';
import { VDriveFleetOrganization } from 'components/src/types/api/vDrive.types';
import { useEffect, useState } from 'react';
import { VDriveFleetModal } from './VDriveFleetModal';
import { isFleetIntegrationNotConfigured } from './vDriveHelpers';

/** What the vDrive GET gives back about a pin: the code and the saved name. */
export type PinnedFleet = { id: string; name: string | null };

/** The pin out of a location or brand vDrive, `null` when not pinned. */
export const pinnedFleetOf = (
  vDrive?: {
    vdriveOrganizationId?: string | null;
    vdriveOrganizationName?: string | null;
  } | null
): PinnedFleet | null =>
  vDrive?.vdriveOrganizationId
    ? {
        id: vDrive.vdriveOrganizationId,
        name: vDrive.vdriveOrganizationName ?? null,
      }
    : null;

type Props = {
  value: PinnedFleet | null;
  onChange: (fleet: PinnedFleet | null) => void;
  /** Shown with the value but locked, e.g. a brand copying its location. */
  disabled?: boolean;
  /** The pinned fleet switched vGrubs off or is gone (location status route). */
  unlinked?: boolean;
  helperText: string;
  /**
   * Told once the environment turns out to have no vDrive key: the field
   * hides itself and the caller should stop sending it.
   */
  onUnavailable: () => void;
};

/**
 * The vDrive fleet a location or brand is pinned to: a field that opens
 * VDriveFleetModal, like the dropoff address on Create Manual Delivery.
 */
export const VDriveFleetPicker = ({
  value,
  onChange,
  disabled,
  unlinked,
  helperText,
  onUnavailable,
}: Props) => {
  const [open, setOpen] = useState(false);
  // The whole list, loaded once: tells whether vDrive is set up here at all
  // and whether the pinned fleet has a price list.
  const [allFleets, setAllFleets] = useState<VDriveFleetOrganization[]>([]);
  const [picked, setPicked] = useState<VDriveFleetOrganization | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    getVDriveFleetOrganizations({ deliveryScope: 'own' })
      .then(({ data }) => setAllFleets(data.data))
      .catch((error) => {
        if (isFleetIntegrationNotConfigured(error)) {
          setUnavailable(true);
          onUnavailable();
          return;
        }
        // Not worth a toast on page open; the modal reports it if it recurs.
        reportError(error, { silent: true });
      });
  }, []);

  if (unavailable) return null;

  const selected =
    value &&
    (picked?.id === value.id
      ? picked
      : allFleets.find(({ id }) => id === value.id) ?? null);
  const noPriceList = selected?.quotable === false;

  const handlePick = (fleet: VDriveFleetOrganization) => {
    setPicked(fleet);
    onChange({ id: fleet.id, name: fleet.name });
    setOpen(false);
  };

  return (
    <Div>
      <AdminInputPlaceholder
        label="vDrive Fleet"
        tooltip="The restaurant's own vDrive fleet. Every vDrive delivery asks it first and it always gets the delivery when it gives a quote. When it can't, the other fleets and providers quote as usual."
        placeholder="Not pinned"
        value={
          value ? (value.name ? `${value.name} · ${value.id}` : value.id) : null
        }
        $textCenter
        onClick={disabled ? undefined : () => setOpen(true)}
        onClickClear={disabled ? undefined : () => onChange(null)}
        aria-haspopup="dialog"
      />
      {/* Sits under the field like the receivables link under Reserve Limit. */}
      <Disclaimer $m="0" $mt="8" $center $color={lightTheme.darkGray}>
        {helperText}
      </Disclaimer>
      {value && unlinked && (
        <Div $mt="12">
          <Callout tone="danger">
            This fleet switched vGrubs off or no longer exists, so it gets no
            deliveries for now. The pin stays and works again once the owner
            turns vGrubs back on.
          </Callout>
        </Div>
      )}
      {value && !unlinked && noPriceList && (
        <Div $mt="12">
          <Callout tone="warning">
            This fleet has no price list yet, so it won&apos;t get deliveries
            until the owner sets one.
          </Callout>
        </Div>
      )}

      <VDriveFleetModal
        isOpen={open}
        onClose={() => setOpen(false)}
        selectedId={value?.id ?? null}
        onPick={handlePick}
      />
    </Div>
  );
};
